import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { Loader2, ArrowRight, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Forgot-password modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success('authenticated');
      navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('not verified')) {
        toast.error(t('auth.verify_code_sent'));
        navigate('/verify-email', { state: { email } });
        return;
      }
      toast.error(formatApiError(detail) || err.message || 'login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success(t('auth.forgot_sent'));
      setForgotOpen(false);
      setForgotEmail('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 404) {
        toast.error(t('auth.forgot_no_account'));
      } else if (err.response?.status === 429) {
        toast.error(t('auth.forgot_rate_limit'));
      } else {
        toast.error(formatApiError(detail) || err.message || 'failed');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="login-page">
      <Navbar />
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="text-[10px] text-primary tracking-wider mb-4">$ auth --login</div>
          <h1 className="text-xl font-bold tracking-tight mb-1" data-testid="login-title">{t('auth.login_title')}</h1>
          <p className="text-[11px] text-muted-foreground mb-8">{t('auth.login_subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com" required dir="ltr" data-testid="login-email-input"
                className="h-10 rounded bg-card border-border/50 text-xs" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.password')}</Label>
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                  className="text-[10px] text-primary/80 hover:text-primary tracking-wider transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgot_password')}
                </button>
              </div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required dir="ltr" data-testid="login-password-input"
                className="h-10 rounded bg-card border-border/50 text-xs" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-10 rounded text-[11px] tracking-wider">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>log in <ArrowRight className="ms-1.5 h-3 w-3" /></>}
            </Button>
          </form>

          <div className="mt-4">
            <GoogleSignInButton />
          </div>

          <p className="text-[11px] text-muted-foreground mt-6 text-center">
            {t('auth.no_account')}{' '}
            <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">{t('auth.register_link')}</Link>
          </p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="rounded border-border/50 bg-card/95 backdrop-blur-md max-w-sm" data-testid="forgot-password-dialog">
          <DialogHeader>
            <div className="text-[10px] text-primary tracking-wider mb-1">$ auth --forgot</div>
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              {t('auth.forgot_title')}
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              {t('auth.forgot_subtitle')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgot} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-muted-foreground tracking-wider">{t('auth.email')}</Label>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@gmail.com"
                required
                dir="ltr"
                data-testid="forgot-email-input"
                className="h-9 rounded bg-background/50 border-border/50 text-xs"
                autoFocus
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded h-9 text-[11px]" onClick={() => setForgotOpen(false)}>
                {t('dashboard.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                data-testid="forgot-submit-btn"
                className="rounded h-9 text-[11px] tracking-wider"
              >
                {forgotLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('auth.forgot_send')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
